export function formatInvoiceAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numeric);
}

export function parseServiceMonth(yyyyMm) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(yyyyMm ?? "").trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

export function toServiceMonthKey(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function formatServiceMonthLabel(yyyyMm) {
  const parsed = parseServiceMonth(yyyyMm);
  if (!parsed) {
    return String(yyyyMm ?? "").trim() || "—";
  }
  return new Date(parsed.year, parsed.month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function isWeekday(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function countBusinessDays(start, end) {
  let count = 0;
  const cursor = startOfDay(start);
  const last = startOfDay(end);
  while (cursor <= last) {
    if (isWeekday(cursor)) {
      count += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/** Mon–Fri business days for a calendar month (no holiday logic). */
export function getBusinessDayStats(year, month, referenceDate = new Date()) {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const ref = startOfDay(referenceDate);
  const effectiveEnd =
    ref < monthStart ? monthStart : ref > monthEnd ? monthEnd : ref;

  const total = countBusinessDays(monthStart, monthEnd);
  const passed = countBusinessDays(monthStart, effectiveEnd);
  const remaining = Math.max(0, total - passed);

  return { total, passed, remaining };
}

/**
 * YTD annual projection: average non-draft revenue across completed months this year,
 * then multiply by 12 for a full-calendar-year forecast.
 */
export function computeYtdMonthlyProjection(ytdPriorMonthsTotal, currentMonth) {
  const priorTotal = Number(ytdPriorMonthsTotal) || 0;
  const previousMonthCount = Math.max(0, currentMonth - 1);

  if (previousMonthCount <= 0) {
    return {
      ytdProjected: 0,
      monthlyAverage: 0,
      previousMonthCount: 0,
      priorMonthsTotal: priorTotal,
    };
  }

  const monthlyAverage = priorTotal / previousMonthCount;
  const ytdProjected = monthlyAverage * 12;

  return {
    ytdProjected,
    monthlyAverage,
    previousMonthCount,
    priorMonthsTotal: priorTotal,
  };
}

/**
 * Run-rate projection: spread month-to-date revenue evenly across elapsed business
 * days, then scale to full month (Mon–Fri only).
 */
export function computeRunRateProjection(monthToDate, year, month, referenceDate = new Date()) {
  const { total, passed, remaining } = getBusinessDayStats(year, month, referenceDate);
  const mtd = Number(monthToDate) || 0;

  if (total <= 0 || passed <= 0) {
    return {
      projectedTotal: mtd,
      dailyAverage: 0,
      businessDaysPassed: passed,
      businessDaysTotal: total,
      businessDaysRemaining: remaining,
      monthToDate: mtd,
    };
  }

  const dailyAverage = mtd / passed;
  const projectedTotal = passed >= total ? mtd : dailyAverage * total;

  return {
    projectedTotal,
    dailyAverage,
    businessDaysPassed: passed,
    businessDaysTotal: total,
    businessDaysRemaining: remaining,
    monthToDate: mtd,
  };
}

function isDraftInvoice(invoice) {
  return String(invoice?.status ?? "").trim().toLowerCase() === "draft";
}

function invoiceAmount(invoice) {
  const amount = Number(invoice?.invoiceAmount);
  return Number.isFinite(amount) ? amount : 0;
}

function getCalendarContext(referenceDate = new Date()) {
  const currentYear = referenceDate.getFullYear();
  const currentMonth = referenceDate.getMonth() + 1;
  const currentMonthKey = toServiceMonthKey(currentYear, currentMonth);
  const prevDate = new Date(currentYear, currentMonth - 2, 1);
  const previousMonthKey = toServiceMonthKey(
    prevDate.getFullYear(),
    prevDate.getMonth() + 1
  );
  return { currentYear, currentMonth, currentMonthKey, previousMonthKey };
}

function createBucket() {
  return { total: 0, count: 0 };
}

function addToBucket(bucket, amount) {
  bucket.total += amount;
  bucket.count += 1;
}

/** Stable client key from invoice row (custgroup). */
export function invoiceClientKey(invoice) {
  return String(invoice?.custgroup ?? "").trim();
}

export function invoiceMatchesClient(invoice, clientCustgroup) {
  const filter = String(clientCustgroup ?? "").trim();
  if (!filter) {
    return false;
  }
  return invoiceClientKey(invoice).toLowerCase() === filter.toLowerCase();
}

/** Unique clients from invoice list for dashboard selector. */
export function buildDashboardClientOptions(invoices) {
  const map = new Map();
  for (const invoice of invoices) {
    const custgroup = invoiceClientKey(invoice);
    if (!custgroup) {
      continue;
    }
    const key = custgroup.toLowerCase();
    if (!map.has(key)) {
      const clientName = String(invoice?.clientName ?? "").trim();
      map.set(key, {
        custgroup,
        label: clientName || custgroup,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

function filterInvoicesForClient(invoices, clientCustgroup) {
  if (!clientCustgroup) {
    return invoices;
  }
  return invoices.filter((invoice) => invoiceMatchesClient(invoice, clientCustgroup));
}

export function computeDashboardRevenue(
  invoices,
  view = "current",
  referenceDate = new Date(),
  clientCustgroup = null
) {
  const scopedInvoices = filterInvoicesForClient(invoices, clientCustgroup);
  const { currentYear, currentMonth, currentMonthKey, previousMonthKey } =
    getCalendarContext(referenceDate);

  const ytdPriorMonths = createBucket();
  const currentMonthRecognizedBucket = createBucket();
  const currentMonthAll = createBucket();
  const previousMonth = createBucket();

  let currentMonthRecognized = 0;
  let currentMonthDraft = 0;

  for (const invoice of scopedInvoices) {
    const sm = parseServiceMonth(invoice.serviceMonth);
    if (!sm) {
      continue;
    }

    const amount = invoiceAmount(invoice);
    const key = invoice.serviceMonth;
    const draft = isDraftInvoice(invoice);

    if (sm.year === currentYear && sm.month >= 1 && sm.month < currentMonth && !draft) {
      addToBucket(ytdPriorMonths, amount);
    }

    if (key === currentMonthKey) {
      if (draft) {
        currentMonthDraft += amount;
      } else {
        currentMonthRecognized += amount;
        addToBucket(currentMonthRecognizedBucket, amount);
      }
      addToBucket(currentMonthAll, amount);
    }

    if (key === previousMonthKey && !draft) {
      addToBucket(previousMonth, amount);
    }
  }

  const base = {
    currentMonthRecognized,
    currentMonthDraft,
    previousMonthRevenue: previousMonth.total,
    previousMonthCount: previousMonth.count,
    currentMonthKey,
    previousMonthKey,
    currentYear,
    monthToDateRevenue: currentMonthRecognized + currentMonthDraft,
    dailyAverage: 0,
    businessDaysPassed: 0,
    businessDaysTotal: 0,
    businessDaysRemaining: 0,
    isRunRateProjection: false,
    ytdMonthlyAverage: 0,
    ytdPreviousMonthCount: 0,
    ytdPriorMonthsTotal: 0,
    isYtdMonthlyProjection: false,
  };

  if (view === "projected") {
    const projection = computeRunRateProjection(
      currentMonthRecognized + currentMonthDraft,
      currentYear,
      currentMonth,
      referenceDate
    );
    const ytdProjection = computeYtdMonthlyProjection(ytdPriorMonths.total, currentMonth);

    return {
      ...base,
      yearToDate: ytdProjection.ytdProjected,
      yearToDateCount: ytdPriorMonths.count,
      currentMonthForecast: projection.projectedTotal,
      currentMonthForecastCount: currentMonthAll.count,
      monthToDateRevenue: projection.monthToDate,
      dailyAverage: projection.dailyAverage,
      businessDaysPassed: projection.businessDaysPassed,
      businessDaysTotal: projection.businessDaysTotal,
      businessDaysRemaining: projection.businessDaysRemaining,
      isRunRateProjection: true,
      ytdMonthlyAverage: ytdProjection.monthlyAverage,
      ytdPreviousMonthCount: ytdProjection.previousMonthCount,
      ytdPriorMonthsTotal: ytdProjection.priorMonthsTotal,
      isYtdMonthlyProjection: true,
    };
  }

  return {
    ...base,
    yearToDate: ytdPriorMonths.total + currentMonthRecognizedBucket.total,
    yearToDateCount: ytdPriorMonths.count + currentMonthRecognizedBucket.count,
    currentMonthForecast: currentMonthAll.total,
    currentMonthForecastCount: currentMonthAll.count,
  };
}

/** Non-draft revenue by service month for the current calendar year (Jan → current month). */
export function computeMonthlyRevenueBreakdown(
  invoices,
  referenceDate = new Date(),
  clientCustgroup = null
) {
  const scopedInvoices = filterInvoicesForClient(invoices, clientCustgroup);
  const { currentYear, currentMonth } = getCalendarContext(referenceDate);

  const totalsByMonth = new Map();
  for (let month = 1; month <= currentMonth; month += 1) {
    totalsByMonth.set(month, 0);
  }

  for (const invoice of scopedInvoices) {
    const sm = parseServiceMonth(invoice.serviceMonth);
    if (!sm || sm.year !== currentYear || sm.month < 1 || sm.month > currentMonth) {
      continue;
    }
    if (isDraftInvoice(invoice)) {
      continue;
    }
    totalsByMonth.set(sm.month, (totalsByMonth.get(sm.month) ?? 0) + invoiceAmount(invoice));
  }

  return Array.from(totalsByMonth.entries()).map(([month, revenue]) => ({
    month,
    monthKey: toServiceMonthKey(currentYear, month),
    label: new Date(currentYear, month - 1, 1).toLocaleDateString("en-US", { month: "short" }),
    revenue,
  }));
}
